// Apolo IDE — useDevPreview (sprint code-preview-engine, E2 + E3 + E4).
//
// Orchestrates the preview pane: resolves the session's working dir, drives the
// dev-server runner (E2), and — in the packaged Electron app — layers the CDP
// DevTools on top (E3): the BrowserView is attached as the visible host while the
// rail is open, and console/network events stream into ring buffers. Design Mode
// (E4) toggles the in-guest element picker and exposes "send to agent" + ephemeral
// CSS tweak. Outside Electron every CDP call is a no-op and the panel keeps the
// <iframe> fallback (visual preview only, no devtools).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAgentStore, clampPreviewWidth } from '../store/useAgentStore.js';
import {
  elementToAgentPrompt, elementToLabel, todoToAgentPrompt, todoToLabel,
} from '../lib/designContext.js';
import { useDevServer } from './useDevServer.js';
import { getDevice } from '../components/code/preview/DevicePresets.js';
import {
  resolvePreviewRoot,
  capturePreviewScreenshot,
  normalizePreviewUrl,
  isPreviewCdp,
  previewDetach,
  previewInspect,
  previewTweak,
  previewSetDevice,
  previewSetPick,
  previewSetAnnotate,
  onPreviewConsole,
  onPreviewNetwork,
  onPreviewPicked,
  onPreviewAnnotated,
} from '../lib/previewClient.js';

const RING = 500; // cap console/network buffers — a chatty page can't leak memory

function pushRing(prev, item) {
  const next = prev.concat(item);
  return next.length > RING ? next.slice(next.length - RING) : next;
}

// elementToAgentPrompt (bloco de contexto injetado no prompt) e elementToLabel
// (label curto da PILL) moram em ../lib/designContext.js — compartilhados com o
// parser (parseDesignContent) que a ConversationFeed usa pra re-colapsar o bloco
// numa pill na bolha. Manter os dois juntos evita que os marcadores dessincronizem.

export function useDevPreview(sessionId) {
  const session = useAgentStore(
    (s) => s.code.sessions.find((x) => x.id === sessionId) || null,
  );
  const previewTab = useAgentStore((s) => s.code.ui.previewTab);
  const previewDevice = useAgentStore((s) => s.code.ui.previewDevice);
  const designModeOn = useAgentStore((s) => s.code.ui.designModeOn);
  const annotateModeOn = useAgentStore((s) => s.code.ui.annotateModeOn);
  const previewWidth = useAgentStore((s) => s.code.ui.previewWidth);
  const setPreviewTab = useAgentStore((s) => s.setPreviewTab);
  const setPreviewDevice = useAgentStore((s) => s.setPreviewDevice);
  const setPreviewWidth = useAgentStore((s) => s.setPreviewWidth);
  const toggleDesignMode = useAgentStore((s) => s.toggleDesignMode);
  const toggleAnnotateMode = useAgentStore((s) => s.toggleAnnotateMode);

  const root = resolvePreviewRoot(session);
  const dev = useDevServer(sessionId);
  const [manualUrl, setManualUrl] = useState('');
  const [committedUrl, setCommittedUrl] = useState('');
  // Navigate only on COMMIT (Enter/blur), never per keystroke: typing the URL
  // char-by-char fired a loadURL on every change, and the chain of ERR_ABORTED
  // navigations meant the final URL never finished loading (blank rail).
  const commitUrl = useCallback(() => setCommittedUrl(manualUrl.trim()), [manualUrl]);

  // Auto-commit 600ms after the user stops typing (in addition to Enter/blur). A
  // single debounced commit → a single loadURL → no ERR_ABORTED chain, and it
  // doesn't depend on Enter/blur firing.
  useEffect(() => {
    const id = setTimeout(() => setCommittedUrl(manualUrl.trim()), 600);
    return () => clearTimeout(id);
  }, [manualUrl]);

  // The BrowserView host loads ANY url (google.com, localhost:5173, ...); the dev
  // server's detected URL wins when running, else the committed manual URL.
  const effectiveUrl = dev.url || normalizePreviewUrl(committedUrl);

  // Status the user sees: a loaded URL reads "ao vivo" even if the dev-server
  // auto-start failed earlier — the manual URL path is independent of the runner.
  const previewState = dev.state === 'starting' ? 'starting' : effectiveUrl ? 'ready' : dev.state;

  // ── E3 devtools buffers ────────────────────────────────────────────────────
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [networkRequests, setNetworkRequests] = useState([]);
  const [picked, setPicked] = useState(null);
  const [inspectResult, setInspectResult] = useState(null);
  const cdp = isPreviewCdp();

  // Stream console/network/picked while the rail is open (Electron only).
  useEffect(() => {
    if (!cdp || !sessionId) return undefined;
    const offC = onPreviewConsole((p) => setConsoleLogs((prev) => pushRing(prev, p)));
    const offN = onPreviewNetwork((p) => setNetworkRequests((prev) => pushRing(prev, p)));
    const offP = onPreviewPicked((p) => {
      setPicked(p);
      // Cursor/Claude Code/Codex parity: clicar um elemento no preview JÁ joga ele
      // no composer — sem precisar trocar pra aba Inspect nem clicar num botão.
      // (O picker só captura cliques com Design Mode ligado, então isto só dispara
      // quando o usuário está de fato selecionando.)
      if (p && typeof window !== 'undefined') {
        // Vira uma PILL no composer (label curto) + prompt completo escondido pro
        // agente — não despeja o monte de código no chat.
        window.dispatchEvent(new CustomEvent('apolo:designElement', {
          detail: { label: elementToLabel(p), prompt: elementToAgentPrompt(p) },
        }));
        window.dispatchEvent(new CustomEvent('apolo:focusComposer'));
      }
    });
    return () => {
      offC();
      offN();
      offP();
    };
  }, [cdp, sessionId]);

  // When the dev server announces its URL, mirror it into the editable field so
  // there's a single source of truth (the iframe reads it) and the user can see
  // / tweak it. User-typed URLs survive (dev.url only fires on detection).
  useEffect(() => {
    if (dev.url) setManualUrl(dev.url);
  }, [dev.url]);

  // The BrowserView lifecycle (attach + position + detach) is owned by
  // PreviewViewport on the Preview tab — a SINGLE owner avoids the cross-component
  // race that left the native view stuck off-screen. Here we only make sure a
  // stray view (left by the agent's web tools, or after the URL clears) is
  // detached when there's nothing to preview.
  useEffect(() => {
    if (cdp && !effectiveUrl) previewDetach();
  }, [cdp, effectiveUrl]);

  // 2026-07-10 (Pedro) — o Design Mode VOLTOU ao toolbar: um único ícone de pointer
  // com dropdown "Selecionar 1" (este picker → pill #489) / "Anotar vários". O
  // force-off de mount que vivia aqui saiu junto: havia botão nenhum pra desligar;
  // agora o dropdown desliga. A exclusão mútua (um modo por vez) é do toolbar.

  // Toggle the in-guest element picker with Design Mode. Anotar tem precedência
  // (os dois modos capturam clique no guest — um por vez; o script guest também
  // desliga o irmão, mas o renderer nem tenta ligar os dois).
  useEffect(() => {
    if (!cdp || !effectiveUrl) return undefined;
    previewSetPick({ enabled: !!designModeOn && !annotateModeOn });
    return () => {
      previewSetPick({ enabled: false });
    };
  }, [cdp, effectiveUrl, designModeOn, annotateModeOn]);

  // Modo Anotar (to-dos numerados, paridade Cursor browser): liga o script guest;
  // cada anotação salva chega via onPreviewAnnotated e vira uma pill numerada no
  // composer (MESMO pipeline apolo:designElement → blocos concatenados → UM
  // prompt único no envio). Sem focusComposer por anotação — o usuário continua
  // na página selecionando os próximos elementos.
  useEffect(() => {
    if (!cdp || !effectiveUrl) return undefined;
    previewSetAnnotate({ enabled: !!annotateModeOn });
    return () => {
      previewSetAnnotate({ enabled: false });
    };
  }, [cdp, effectiveUrl, annotateModeOn]);

  useEffect(() => {
    if (!cdp || !sessionId) return undefined;
    const offA = onPreviewAnnotated((p) => {
      if (!p || typeof window === 'undefined') return;
      window.dispatchEvent(new CustomEvent('apolo:designElement', {
        detail: { label: todoToLabel(p.n, p.comment), prompt: todoToAgentPrompt(p, p.comment, p.n) },
      }));
    });
    // O composer avisa quando o prompt único foi ENVIADO → limpa os badges da
    // página e zera a numeração (próxima rodada recomeça no 1).
    const onSubmitted = () => { previewSetAnnotate({ clear: true }); };
    window.addEventListener('apolo:designSubmitted', onSubmitted);
    return () => {
      offA();
      window.removeEventListener('apolo:designSubmitted', onSubmitted);
    };
  }, [cdp, sessionId]);

  // BUG-1 — actually DRIVE the BrowserView's viewport from the device toggle. The
  // toggle used to only write the store; the CDP `Emulation.setDeviceMetricsOverride`
  // wrapper existed but was never called, so in the packaged app switching
  // desktop/tablet/mobile did nothing (responsive worked ONLY in the web <iframe>
  // fallback). Mirror that fallback's fit-to-rail scale into CDP so the packaged
  // app emulates the viewport. Re-runs on rail resize too, keeping the fit.
  useEffect(() => {
    if (!cdp || !effectiveUrl) return;
    const preset = getDevice(previewDevice);
    if (!preset.width || !preset.height) {
      previewSetDevice({}); // responsive → clear override (fill the rail natively)
      return;
    }
    const railW = clampPreviewWidth(previewWidth ?? 360) - 12; // same LEFT inset PreviewViewport uses
    const scale = Math.min(1, railW > 0 ? railW / preset.width : 1);
    previewSetDevice({
      width: preset.width,
      height: preset.height,
      dpr: preset.dpr,
      mobile: previewDevice !== 'desktop',
      scale,
    });
  }, [cdp, effectiveUrl, previewDevice, previewWidth]);

  const start = useCallback(() => dev.start({ root }), [dev, root]);
  const stop = useCallback(() => dev.stop(), [dev]);
  const restart = useCallback(async () => {
    await dev.stop();
    return dev.start({ root });
  }, [dev, root]);
  const screenshot = useCallback(() => capturePreviewScreenshot(), []);

  const clearConsole = useCallback(() => setConsoleLogs([]), []);
  const clearNetwork = useCallback(() => setNetworkRequests([]), []);

  const inspect = useCallback(async (selector) => {
    const res = await previewInspect({ selector });
    setInspectResult(res?.ok ? res : null);
    return res;
  }, []);

  const applyTweak = useCallback((t) => previewTweak(t), []);

  // E4 — dispatch the element's context into the session composer (the composer
  // listens for 'apolo:insertComposer' and appends + focuses).
  const sendToAgent = useCallback((el) => {
    const target = el || picked || inspectResult;
    if (!target) return false;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('apolo:designElement', {
        detail: { label: elementToLabel(target), prompt: elementToAgentPrompt(target) },
      }));
      window.dispatchEvent(new CustomEvent('apolo:focusComposer'));
    }
    return true;
  }, [picked, inspectResult]);

  const cdpRef = useRef(cdp);
  cdpRef.current = cdp;

  // Detach the shared BrowserView when the panel closes (hook unmount) — covers
  // closing the panel while on a non-Preview tab (PreviewViewport handles the
  // tab-switch case). Empty deps → cleanup runs only on unmount.
  useEffect(() => () => { if (cdpRef.current) previewDetach(); }, []);

  return {
    session,
    root,
    state: previewState,
    url: effectiveUrl,
    log: dev.log,
    supported: dev.supported,
    cdp,
    manualUrl,
    setManualUrl,
    commitUrl,
    tab: previewTab ?? 'preview',
    setTab: setPreviewTab,
    device: previewDevice ?? 'desktop',
    setDevice: setPreviewDevice,
    width: clampPreviewWidth(previewWidth ?? 360),
    setWidth: setPreviewWidth,
    designModeOn: Boolean(designModeOn),
    toggleDesignMode,
    annotateModeOn: Boolean(annotateModeOn),
    toggleAnnotateMode,
    // E3
    consoleLogs,
    networkRequests,
    clearConsole,
    clearNetwork,
    inspect,
    inspectResult,
    // E4
    picked,
    applyTweak,
    sendToAgent,
    // actions
    start,
    stop,
    restart,
    screenshot,
  };
}
